import { ApiError } from "@models/error";
import type {
  IMappingJobStatusResponse,
  IMappingJobViewModel,
} from "@models/mapping";
import { buildMappingJobViewModelFromStatus, HttpClient } from "@utils/index";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

if (!BASE_URL) {
  // The extraction UI works without mapping configured — it simply never
  // shows mapping progress. Warn rather than throw so the app still boots.
  console.warn(
    "VITE_MAPPING_API_BASE_URL is not set. Mapping status will not be shown " +
      "next to extraction results until it is configured in .env.",
  );
}

/**
 * Thin REST client for the xplatform mapping service. Used as the
 * initial-load / reconnect fallback for the live notification socket: the
 * extraction FE does not know the mapping job_id (it is generated async
 * server-side), so jobs are looked up by `process_id` + `process_item_id`
 * (both are available wherever a result item is rendered), served off the
 * mapping service's ByProcess GSI.
 */
class MappingApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(BASE_URL ?? "");
  }

  /**
   * GET /jobs?process_id=...&process_item_id=... → the current mapping job for
   * an extraction result item, or `null` when no job has been dispatched yet
   * (404). `process_id` is required so the lookup is an indexed Query rather
   * than a table scan.
   */
  public async getMappingJobByProcessItem(
    processId: string,
    processItemId: string,
  ): Promise<IMappingJobViewModel | null> {
    if (!BASE_URL) {
      return null;
    }
    const params = new URLSearchParams({
      process_id: processId,
      process_item_id: processItemId,
    });
    try {
      const response =
        await this.httpClient.makeAuthenticatedRequest<IMappingJobStatusResponse>(
          `/jobs?${params.toString()}`,
          { method: "GET" },
        );
      if (!response) {
        return null;
      }
      return buildMappingJobViewModelFromStatus(response, processItemId);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }
}

export { MappingApi };

export const mappingApi = new MappingApi();
